import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { isMobile } from 'react-device-detect';
import { useQuery } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  getAdminSetting,
  getAssuranceProducts,
  getBanner,
  getCounts,
  getInterestProducts,
  getLiveStream,
  getRecommendProducts,
  getYoutubeVideos,
} from '@/api/public';
import BannerCarousel from '@/components/Common/Banner/BannerCarousel';
import TopBanner from '@/components/Common/Banner/TopBanner';
import BaseModal from '@/components/Common/Modal/BaseModal';
import AppUpdateFullscreenPopup from '@/components/Home/AppUpdateFullscreenPopup';
import AssuranceProduts from '@/components/Home/AssuranceProducts';
import InterestProducts from '@/components/Home/InterestProducts';
import RecommendProducts from '@/components/Home/RecommendProducts';
import ReviewTicker from '@/components/Home/ReviewTicker';
import SubCards from '@/components/Home/SubCards';
import LiveMinimalAfter from '@/components/Home/YoutubeLive/LiveMinimalAfter';
import LiveMinimalBefore from '@/components/Home/YoutubeLive/LiveMinimalBefore';
import YoutubeSection from '@/components/Home/YoutubeSection';
import {
  BellIcon,
  CalendarCheckColorIcon,
  IncreasingTrendGraphColorIcon,
  JobBoldColorIcon,
  LicensePlateBoldColorIcon,
  PhoneRingOutlineIcon,
  PhoneRingSolidIcon,
} from '@/components/Icon';
import SvgSearchQuestionBoldIcon from '@/components/Icon/SearchQuestionBoldIcon';
import { HOME, HOME_POP_UP } from '@/const/banner';
import { colors } from '@/const/colors';
import { REPRESENTATIVE_NUMBER } from '@/const/common';
import { MAINTENANCE_SALE, SALE } from '@/const/productStatus';
import { IMAGE_BASE_URL } from '@/const/url';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { isMemberLoadedAtom, memberAtom } from '@/store/member';
import { isExistNotReadNotificationAtom } from '@/store/notification';
import { phoneCall } from '@/utils/common';

const businessInfo = [
  { title: '상호', content: '주식회사 직트럭' },
  { title: '대표', content: '양요한' },
  { title: '주소', content: '경기도 평택시 월곡길 9-9' },
  { title: '대표 전화', content: REPRESENTATIVE_NUMBER },
  { title: '사업자 번호', content: '721-87-01975' },
  { title: '통신판매업 신고번호', content: '제 2023-경기평택-1174호' },
];

interface Counts {
  jobCount: number;
  licenseCount: number;
  productsCount: number;
}

const Home = () => {
  const [memberAtomData] = useAtom(memberAtom);
  const [isMemberLoaded] = useAtom(isMemberLoadedAtom);
  const [isExistNotReadNotification] = useAtom(isExistNotReadNotificationAtom);

  const userAgent = navigator.userAgent;

  const isNaverInApp = /NAVER/.test(userAgent);
  const isKakaoInApp = /KAKAOTALK/.test(userAgent);

  const isAndroidWeb =
    /Android/i.test(userAgent) && /WebKit/i.test(userAgent) && (!/wv/i.test(userAgent) || isNaverInApp || isKakaoInApp);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceToken = searchParams.get('deviceToken');

  const LOCALSTORAGE_KEY = 'homePopupTime';

  const checkPopupClose = () => {
    const expireDay = localStorage.getItem(LOCALSTORAGE_KEY);
    const today = new Date();

    return today.getTime() <= Number(expireDay);
  };

  const [isShowHomePopup, setIsShowHomePopup] = useState(() => !checkPopupClose());
  const [isShowAppUpdatePopup, setIsShowAppUpdatePopup] = useState(false);
  const [isShowCallModal, setIsShowCallModal] = useState(false);
  const [isShowLiveStreamModal, setIsShowLiveStreamModal] = useState(false);

  const [counts, setCounts] = useState<Counts>({
    jobCount: 0,
    licenseCount: 0,
    productsCount: 0,
  });

  const [isShowTopBanner, setIsShowTopBanner] = useState(false);
  // 라이브 방송 노출 안 할 때는 true로 변경
  const [isLiveMinimalHidden, setIsLiveMinimalHidden] = useState(true);
  // 라이브 방송 중일 때는 false로 변경
  const [isLiveBefore, setIsLiveBefore] = useState(true);
  const [liveStreamHour, setLiveStreamHour] = useState<string>('');
  const [liveStreamMinute, setLiveStreamMinute] = useState<string>('');
  const [liveStreamVideoUrl, setLiveStreamVideoUrl] = useState<string>('');
  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    content: null,
    onClose: () => {},
    isXMarkVisible: false,
    paddingType: 'none',
  });

  const subFunctionCards: ButtonCard[] = [
    {
      title: '시세검색',
      subTitle: '시세검색',
      path: '/price-trend/form',
      icon: 'chart_gra.gif',
    },
    {
      title: '번호판거래',
      subTitle: '번호판거래',
      path: '/license',
      icon: 'license_gra.gif',
    },
    {
      title: '운행일지',
      subTitle: '운행일지',
      path: '/drive',
      icon: 'diary_gra.gif',
    },
    {
      title: '일자리구하기',
      subTitle: '일자리구하기',
      path: '/job',
      icon: 'bag_gra.gif',
    },
    {
      title: '알선판매 서비스',
      subTitle: '거래절차가 복잡하다면?',
      path: '/one-stop-service',
      icon: 'sale_car_gra.gif',
    },
    {
      title: '구매동행 서비스',
      subTitle: '차량을 안전하게 구매하고 싶으시다면?',
      path: '/purchase-accompanying-service',
      icon: 'truck_gra.gif',
    },
  ];

  useEffect(() => {
    if (isAndroidWeb && !isMobile) {
      setIsShowTopBanner(true);
    }

    if (deviceToken) {
      localStorage.setItem('deviceToken', deviceToken);
    }
  }, []);

  const queryOptions = {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  };

  const {
    data: bannersData = [],
    isLoading: bannersLoading,
    isError: bannersError,
  } = useQuery('banners', getBanner, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    cacheTime: 1000 * 60 * 24,
  });

  const filteredBanners = bannersData.filter((banner: Banner) => banner.bannerLocation.code === HOME);
  const randomPopupImage = useMemo(() => {
    const popups = bannersData.filter((banner: Banner) => banner.bannerLocation.code === HOME_POP_UP);

    if (popups.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * popups.length);
    return popups[randomIndex];
  }, [bannersData]);

  const { data: adminSetting } = useQuery('get-admin-setting', getAdminSetting, {
    ...queryOptions,
    retry: false,
  });

  // bannerAutoScroll이 1일 때만 수동 스크롤, 0·null·조회 실패 시에는 자동 스크롤
  const isBannerAutoScroll = adminSetting?.bannerAutoScroll !== 1;

  const {
    data: countsData,
    isLoading: countsLoading,
    isError: countsError,
  } = useQuery<Counts>('get-counts', getCounts, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // console.log('countsData', countsData);
    if (countsData) {
      setCounts({
        jobCount: countsData.jobCount,
        licenseCount: countsData.licenseCount,
        productsCount: countsData.productsCount,
      });
    }
  }, [countsData]);

  const {
    data: liveStreamData,
    isLoading: liveStreamLoading,
    isError: liveStreamError,
  } = useQuery('get-live-stream', getLiveStream, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!liveStreamLoading && liveStreamData) {
      if (liveStreamData.liveStatus === 'Y') {
        setIsLiveMinimalHidden(false);
        if (liveStreamData.liveYn === 'N') {
          setIsLiveBefore(true);
        } else {
          setIsLiveBefore(false);
        }
      } else {
        setIsLiveMinimalHidden(true);
      }

      if (liveStreamData.liveUrl) {
        setLiveStreamVideoUrl(liveStreamData.liveUrl);
      }

      if (liveStreamData.livePreview) {
        const [hour, minute] = liveStreamData.livePreview.split(':');
        setLiveStreamHour(hour);
        setLiveStreamMinute(minute);
      }
    }
  }, [liveStreamData, liveStreamLoading]);

  const {
    data: assuranceProducts = { data: [], totalElements: 0, totalPages: 0 },
    isLoading: assuranceProductsLoading,
    isError: assuranceProductsError,
  } = useQuery('get-assurance-products', getAssuranceProducts, queryOptions);

  const {
    data: recommendProducts = [],
    isLoading: recommendProductsLoading,
    isError: recommendProductsError,
  } = useQuery(['get-recommend-products'], () => getRecommendProducts(), {
    ...queryOptions,
    enabled: !!isMemberLoaded && !!memberAtomData?.id,
  });

  const {
    data: youtubeData = [],
    isLoading: youtubeLoading,
    isError: youtubeError,
  } = useQuery('get-youtube', getYoutubeVideos, queryOptions);

  const getMyInterestProducts = async () => {
    try {
      const response = await getInterestProducts(SALE + ',' + MAINTENANCE_SALE);
      const res: ProductsList[] = response;
      return res;
    } catch (error) {
      throw new Error('Error');
    }
  };

  const {
    data: interestProducts = [],
    isLoading: interestProductsLoading,
    isError: interestProductsError,
  } = useQuery('get-interest-products', getMyInterestProducts, {
    ...queryOptions,
    enabled: !!isMemberLoaded && !!memberAtomData?.id,
  });

  const closePopup = (expireDays: number) => {
    let expire = new Date();
    expire.setTime(expire.getTime() + expireDays * 24 * 60 * 60 * 1000);
    localStorage.setItem(LOCALSTORAGE_KEY, expire.getTime().toString());
  };

  const onClickCloseToday = () => {
    closePopup(1);
    setIsShowHomePopup(false);
  };

  const onClickClose = () => {
    setIsShowHomePopup(false);
  };

  const onClickPurchaseInquiry = () => {
    navigate('/products/purchase');
  };

  const onClickImage = (item?: Banner | null) => {
    if (item?.link) {
      if (item.type.code === 'INTERNAL') {
        navigate(item.link);
      } else {
        window.open(item.link);
      }
    }
  };

  const onClickCallInquiry = () => {
    if (isMobile) {
      phoneCall(REPRESENTATIVE_NUMBER);
    } else {
      setIsShowCallModal(true);
      setModalConfig({
        content: renderCallInquiryModal(),
        isXMarkVisible: true,
        paddingType: 'padding-top-20',
        onClose: () => setIsShowCallModal(false),
      });
    }
  };

  const renderCallInquiryModal = () => {
    return (
      <div className="flex flex-col items-right w-full px-2 pb-[14px]">
        <span className="font-medium text-gray-8 text-left text-[14px]">궁금한 사항이 있으신가요?</span>
        <div className="flex items-end justify-between text-left">
          <div className="flex flex-col gap-1 mt-5">
            <p className="font-semibold text-[22px] text-gray-12">전화문의</p>
            <p className="font-semibold text-[30px] text-gray-12 mt-1">1599-6249</p>
          </div>
          <PhoneRingSolidIcon color={colors.primary[10]} width={58} height={58}></PhoneRingSolidIcon>
        </div>
      </div>
    );
  };

  const renderLiveStreamModal = () => {
    return (
      <div className="flex flex-col items-right w-full px-2 pb-[14px]">
        <span className="font-medium text-gray-8 text-center text-[16px] mb-2">방송이 준비중입니다.</span>
        <span className="font-medium text-gray-7 text-center text-[14px]">잠시 후 다시 시도해주세요.</span>
      </div>
    );
  };

  const handleClickLiveStream = () => {
    if (liveStreamData?.liveYn === 'N' && liveStreamData?.livePreview) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      const [previewHour, previewMinute] = liveStreamData.livePreview.split(':').map(Number);
      const previewTime = previewHour * 60 + previewMinute;
      const currentTime = currentHour * 60 + currentMinute;

      // livePreview가 현재 시간보다 이후일 때만 모달 표시
      if (previewTime < currentTime) {
        setIsShowLiveStreamModal(true);
      }
    } else {
      setIsShowLiveStreamModal(true);
    }
  };

  const renderHomeModal = (): React.ReactNode => {
    return (
      <div className="w-full">
        <img
          loading="lazy"
          src={randomPopupImage?.contents || ''}
          alt="popup_image"
          className="block w-full h-auto max-h-[70dvh] object-contain"
          onClick={() => onClickImage(randomPopupImage)}
        />
        <div className="flex items-center justify-between px-4 py-3">
          <p
            className="text-[16px] sm280:text-[14px] cursor-pointer text-gray-6 font-medium"
            onClick={onClickCloseToday}
          >
            오늘 하루 보지 않기
          </p>
          <p
            className="font-semibold cursor-pointer text-primary-10 text-[18px] sm280:text-[16px]"
            onClick={onClickClose}
          >
            닫기
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <DefaultHelmet page="home" />
      <div className="w-full">
        <div className={`pb-16 `}>
          <div className={`${isLiveMinimalHidden ? '' : 'h-[95px] bg-gray-2 sm280:h-[120px]'}`}></div>
          {!isLiveMinimalHidden &&
            (isLiveBefore ? (
              <LiveMinimalBefore
                isShowTopBanner={isShowTopBanner}
                liveStreamHour={liveStreamHour}
                liveStreamMinute={liveStreamMinute}
                liveStreamVideoUrl={liveStreamVideoUrl}
                onClickLiveStream={handleClickLiveStream}
              />
            ) : (
              <LiveMinimalAfter isShowTopBanner={isShowTopBanner} liveStreamVideoUrl={liveStreamVideoUrl} />
            ))}

          <div
            className={`flex flex-col pb-[10px] bg-gradient-to-b from-[#F5F5F5] to-[#ffffff] ${
              isShowTopBanner ? 'pt-[109px]' : 'pt-[52px]'
            } `}
          >
            <div
              className={`fixed items-center justify-between flex z-30 ${
                isShowTopBanner ? 'top-[48px]' : 'top-0'
              } mx-auto left-0 right-0 p-4 w-full min-w-[280px] max-w-[720px] max-h-[60px] h-[52px]`}
              style={{
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              <img loading="lazy" src={`${IMAGE_BASE_URL}/logo_gra.png`} alt="직트럭 로고" width={50} height={24} />
              <div className="flex gap-[10px]">
                {/* <div onClick={onClickCallInquiry}>
                  <BubbleComponent
                    text="<strong class='text-gray-0 text-[12px] font-medium'>전화 상담하기</strong>"
                    bgColor={colors.primary[10]}
                    padding="4px 6px"
                    borderRadius="4px"
                    edgeArrow="right"
                    width="82px"
                  />
                </div> */}

                <div
                  className="relative w-[26px] h-[26px] flex justify-center items-center"
                  onClick={onClickCallInquiry}
                >
                  <PhoneRingOutlineIcon color={colors.gray[8]} width={26} height={26} />
                </div>
                <Link to="/notification/#activity" aria-label="알림 확인" role="link" className="flex cursor-pointer">
                  <div className="relative w-[26px] h-[26px] flex justify-center items-center">
                    <BellIcon color={colors.gray[8]} width={26} height={26}></BellIcon>
                    {isExistNotReadNotification && (
                      <div className="absolute top-0 right-0 w-1 h-1 rounded bg-red"></div>
                    )}
                  </div>
                </Link>
              </div>
            </div>

            <BannerCarousel banners={filteredBanners} isAutoScroll={isBannerAutoScroll}></BannerCarousel>

            <div className="mt-[36px] mb-4 px-4">
              <div className="flex flex-col gap-2 sm680:flex-row ">
                <Link
                  to="/sell-car"
                  className="flex-1 flex flex-col justify-center overflow-hidden bg-gray-0 px-[18px] pt-4 rounded-[12px] w-full shadow-soft relative min-h-[100px] "
                >
                  <span className="text-gray-8 font-bold text-[20px] leading-[24px] break-keep">내차판매</span>
                  <span className="text-gray-7 font-medium text-[12px] leading-[15px] mt-[6px] mb-4">
                    1분만에 빠르게
                    <br className="block sm680:hidden" />
                    판매 등록하기
                  </span>
                  <div className="w-[146px] absolute top-[-8px] right-[-6px] ">
                    <img src={`${IMAGE_BASE_URL}/car-sale.png`} alt="내차판매 이미지" />
                  </div>
                </Link>
                <Link
                  to="/products"
                  className="flex-1 flex flex-col justify-center bg-gray-0 px-[18px] pt-4 rounded-[12px] w-full shadow-soft relative min-h-[100px] "
                >
                  <span className="text-gray-8 font-bold text-[20px] leading-[24px] flex items-center gap-1 ">
                    내차구매
                    {counts.productsCount > 0 ? (
                      <motion.button
                        className="p-1 w-6 h-6 absolute top-[-5px] right-[-10px] rounded-full flex items-center justify-center bg-[#FB2C36] text-white text-[12px] font-semibold whitespace-nowrap"
                        animate={{
                          boxShadow: ['0 0 0 0 rgba(239, 68, 68, 0.4)', '0 0 0 10px rgba(239, 68, 68, 0)'],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                        }}
                      >
                        {counts.productsCount}
                      </motion.button>
                    ) : (
                      ''
                    )}
                  </span>
                  <span className="text-gray-7 font-medium text-[12px] leading-[15px] mt-[6px] mb-4">
                    찾고 있는 그 차량,
                    <br className="block sm680:hidden" />
                    여기에 있어요!
                  </span>
                  <div className="w-[146px] absolute top-0 right-0">
                    <img src={`${IMAGE_BASE_URL}/car-purchase.png`} alt="내차판매 이미지" />
                  </div>
                </Link>
              </div>
            </div>

            {/* 내차 구매 문의 
            <div className="mt-[10px] px-4" onClick={onClickPurchaseInquiry}>
              <div className="flex flex-row items-center justify-center w-full bg-gray-0 py-5 px-[18px] rounded-[12px] shadow-soft">
                <div className="flex flex-col flex-1">
                  <span className="font-bold text-[18px] text-gray-8">내차 구매 문의하기</span>
                  <span className="font-medium text-[12px] text-gray-7 break-keep">원하는 차량이 없을 때</span>
                </div>
                <SvgSearchQuestionBoldIcon color={colors.gray[7]} width={40} height={40}></SvgSearchQuestionBoldIcon>
              </div>
            </div> */}
          </div>

          {/* <div className="w-full h-10 bg-gradient-to-b from-[#F5F5F5] to-[#ffffff]"></div> */}

          {/* <div>더 많은 서비스 들어올 자리</div> */}
          <ReviewTicker />
          <SubCards cards={subFunctionCards} />

          <AssuranceProduts products={assuranceProducts} />

          {memberAtomData && interestProducts?.length > 0 && (
            <>
              <InterestProducts products={interestProducts} />
            </>
          )}

          {recommendProducts?.length > 0 && <RecommendProducts products={recommendProducts} />}

          <YoutubeSection data={youtubeData} />

          <div className="w-full px-4 py-5 bg-gray-2">
            <h3 className="text-gray-7 text-[13px] mb-2">직트럭 사업자 정보</h3>
            <div className="text-gray-6 text-[10px] mb-3">
              <dl>
                {businessInfo.map((item, index) => (
                  <div key={index} className="table-row">
                    <dt className="table-cell min-w-[140px]">{item.title}</dt>
                    <dd>{item.content}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="text-gray-6 text-[10px]">
              <p>ⓒ 2020 주식회사 직트럭 All Rights Reserved</p>
              <p>
                주식회사 직트럭은 통신판매중개자로서 통신판매의 당사자가 아니며, 상품 및 거래정보, 거래에 대해 책임을
                지지않습니다.
              </p>
            </div>
          </div>
        </div>

        <AppUpdateFullscreenPopup isOpen={isShowAppUpdatePopup} />

        <BaseModal
          isOpen={isShowHomePopup && !!randomPopupImage}
          content={randomPopupImage ? renderHomeModal() : null}
          isXMarkVisible={false}
          paddingType="none"
          onClose={() => {}}
        />

        <BaseModal
          isOpen={isShowLiveStreamModal}
          content={renderLiveStreamModal()}
          isXMarkVisible={true}
          paddingType="padding-top-20"
          onClose={() => setIsShowLiveStreamModal(false)}
        />

        <BaseModal
          isOpen={isShowCallModal}
          content={renderCallInquiryModal()}
          isXMarkVisible={true}
          paddingType="padding-top-20"
          onClose={() => setIsShowCallModal(false)}
        />

        {!isMobile && <TopBanner isShow={isShowTopBanner}></TopBanner>}
      </div>
    </>
  );
};

export default Home;
